import { useScheduleFreshness } from '../../hooks/useScheduleFreshness';

/**
 * Shown above the schedule when the data hasn't refreshed inside the configured window.
 *
 * Deliberately informational rather than an alarm. The reader is usually a field technician who
 * can't fix an ingest, and the schedule is still broadly correct — jobs run for days. What they
 * need is to know the number they're reading has an age. The people who *can* act get the admin
 * ingest card and an Azure email alert instead.
 *
 * Renders nothing while loading, on error, or when fresh — a banner that flickers in on every
 * page load teaches people to ignore it.
 */
export function StaleScheduleNotice() {
  const { data } = useScheduleFreshness();

  if (!data?.isStale) return null;

  const message =
    data.lastSuccessUtc === null
      ? 'The schedule has never finished loading. Contact your admin.'
      : `Schedule last updated ${formatAge(data.ageHours)} ago.`;

  return (
    <div
      role="status"
      className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
    >
      <span aria-hidden="true" className="mt-px shrink-0 font-semibold">!</span>
      <p className="min-w-0">
        {message}{' '}
        <span className="text-amber-900/70">It may not reflect recent changes.</span>
      </p>
    </div>
  );
}

/** "26 hours" reads worse than "1 day" once you're past a day, which is the only time this shows. */
function formatAge(hours: number | null): string {
  if (hours === null) return 'some time';
  if (hours < 48) return `${hours} hours`;
  return `${Math.floor(hours / 24)} days`;
}
